import React, { useState, useEffect } from 'react';
import { Shield, Edit2, Trash2, Plus, Lock } from 'lucide-react';
import { Role } from '../types';
import { userService } from '../services/user.service';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';

const AVAILABLE_PERMISSIONS = [
  { group: 'Menus', permissions: ['menu:dashboard', 'menu:tracking', 'menu:status', 'menu:users', 'menu:roles'] },
];

export default function RolesPage() {
  const { user: currentUser } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    permissions: string[];
  }>({
    name: '',
    description: '',
    permissions: [],
  });

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const rolesData = await userService.getRoles();
      setRoles(rolesData);
    } catch (error) {
      toast.error('Erro ao carregar perfis');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (role: Role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description,
      permissions: role.permissions?.map(p => typeof p === 'string' ? p : p.name) || [],
    });
    setModalOpen(true);
  };

  const handleCreate = () => {
    setEditingRole(null);
    setFormData({
      name: '',
      description: '',
      permissions: [],
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingRole) {
        await userService.updateRole(editingRole.id, formData);
        toast.success('Perfil atualizado com sucesso');
      } else {
        await userService.createRole(formData);
        toast.success('Perfil criado com sucesso');
      }
      setModalOpen(false);
      await loadRoles();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message || 'Erro ao salvar perfil';
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (role: Role) => {
    if (!window.confirm(`Tem certeza que deseja excluir o perfil ${role.name}?`)) return;

    try {
      await userService.deleteRole(role.id);
      toast.success('Perfil excluído com sucesso');
      loadRoles();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message || 'Erro ao excluir perfil';
      toast.error(errorMsg);
    }
  };

  const togglePermission = (permission: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const isSystemRole = (roleName: string) => {
    return ['super_admin'].includes(roleName);
  };

  // Check if user has permission
  if (!currentUser?.Role || currentUser.Role.name !== 'super_admin') {
    return <Navigate to="/dashboard" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Perfis de Acesso</h1>
          <p className="text-gray-500 mt-1">Gerencie perfis e permissões do sistema</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-5 h-5 mr-2" />
          Novo Perfil
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-3 flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          roles.map((role, index) => (
            <motion.div
              key={`${role.id}-${role.updatedAt}-${role.permissions?.length || 0}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center">
                  <Shield className="w-8 h-8 text-primary-600 mr-3" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{role.name}</h3>
                    <p className="text-sm text-gray-500">{role.description}</p>
                  </div>
                </div>
                {!isSystemRole(role.name) && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(role)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(role)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">Permissões:</div>
                <div className="max-h-32 overflow-y-auto">
                  {role.permissions && role.permissions.length > 0 ? (
                    <div className="space-y-1">
                      {role.permissions.slice(0, 5).map((permission, idx) => (
                        <div key={idx} className="text-xs text-gray-600 flex items-center">
                          <Lock className="w-3 h-3 mr-1" />
                          {typeof permission === 'string' ? permission : permission.name}
                        </div>
                      ))}
                      {role.permissions.length > 5 && (
                        <div className="text-xs text-gray-500 italic">
                          +{role.permissions.length - 5} mais...
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 italic">Sem permissões definidas</p>
                  )}
                </div>
              </div>

              {isSystemRole(role.name) && (
                <div className="mt-4 px-2 py-1 bg-gray-100 rounded text-xs text-gray-600 text-center">
                  Perfil do sistema
                </div>
              )}
            </motion.div>
          ))
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setModalOpen(false)}></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingRole ? 'Editar Perfil' : 'Novo Perfil'}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      disabled={!!(editingRole && isSystemRole(editingRole.name))}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Permissões</label>
                    <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md p-4 space-y-4">
                      {AVAILABLE_PERMISSIONS.map(group => (
                        <div key={group.group}>
                          <h4 className="font-medium text-sm text-gray-900 mb-2">{group.group}</h4>
                          <div className="grid grid-cols-2 gap-2">
                            {group.permissions.map(permission => (
                              <label key={permission} className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={formData.permissions.includes(permission)}
                                  onChange={() => togglePermission(permission)}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm text-gray-700">{permission}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleSave}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Salvar
                </button>
                <button
                  onClick={() => setModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}